// Field — shared labelled form-field wrapper (A11y/M5).
//
// Replaces the per-form copy-pasted `Field` helper that rendered a visible
// label NOT associated with its control. This version gives every control an
// accessible name and wires required/description state:
//
//   - Single native control child (<input>/<select>/<textarea>): a <label
//     htmlFor> is bound to a generated id injected onto the control, plus
//     `aria-required` and `aria-describedby` (→ the hint) when applicable.
//   - Non-control child (e.g. a group of star-rating buttons): the field
//     becomes a labelled `role="group"` (aria-labelledby → the label text),
//     since there's no single element to bind a <label> to.
//
// useId keeps ids unique across multiple Field instances and SSR-safe. The
// markup/layout is unchanged from the old helper — this only adds the
// association attributes.

import {
  useId,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react'

const CONTROL_TAGS = new Set(['input', 'select', 'textarea'])

const LABEL_CLASS = 'block text-sm font-medium text-hoop-text-2 mb-1'
const HINT_CLASS = 'text-xs text-hoop-text-3 mt-1'

interface FieldProps {
  label: string
  hint?: string
  required?: boolean
  children: ReactNode
}

export default function Field({ label, hint, required, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const labelId = `${id}-label`

  const childEl = isValidElement(children)
    ? (children as ReactElement<Record<string, unknown>>)
    : null
  const isNativeControl =
    childEl != null &&
    typeof childEl.type === 'string' &&
    CONTROL_TAGS.has(childEl.type)

  const RequiredMark = required ? (
    <span className="text-hoop-red ml-0.5" aria-hidden="true">
      *
    </span>
  ) : null

  if (isNativeControl && childEl) {
    const control = cloneElement(childEl, {
      id,
      'aria-required': required || undefined,
      'aria-describedby': hintId,
    })
    return (
      <div>
        <label htmlFor={id} className={LABEL_CLASS}>
          {label}
          {RequiredMark}
        </label>
        {control}
        {hint && (
          <p id={hintId} className={HINT_CLASS}>
            {hint}
          </p>
        )}
      </div>
    )
  }

  // Group fallback — the child isn't a single labelable control (e.g. the
  // star-rating button row). Associate the visible label with the group.
  return (
    <div role="group" aria-labelledby={labelId} aria-describedby={hintId}>
      <span id={labelId} className={LABEL_CLASS}>
        {label}
        {RequiredMark}
      </span>
      {children}
      {hint && (
        <p id={hintId} className={HINT_CLASS}>
          {hint}
        </p>
      )}
    </div>
  )
}
